<template>
  <form @submit.prevent="handleSubmit">
    <div class="flex flex-wrap pt-4">
      <select-input
        v-if="!edit"
        v-model="form.subject.type"
        :errors="form.errors.type"
        class="w-full pb-8 pr-6 lg:w-1/2"
        label="Tip"
      >
        <option :value="'VideoSubject'">Video</option>
        <option :value="'DocumentsSubject'">Documents</option>
        <option :value="'FeedbackSubject'">Feedback</option>
        <option :value="'QuizSubject'">Quiz</option>
      </select-input>
      <div v-else class="pb-8">
        <span>{{ 'Tip: ' + subjectTypeName }}</span>
      </div>
      <text-input
        v-model="form.subject.name"
        :errors="form.errors.name"
        class="w-full pb-8 pr-6"
        label="Name"
      />
      <text-input
        v-model="form.position"
        :errors="form.errors.position"
        class="w-full pb-8 pr-6"
        type="Number"
        label="Pozitie"
        :min="1"
        :max="max"
      />
      <template v-if="form.subject.type === 'VideoSubject'">
        <file-input
          v-model="form.video"
          :errors="form.errors.video"
          class="w-full pb-8 pr-6"
          type="file"
          accept="video/*"
          label="Video"
        />
        <template v-if="form.subject.existing_video">
          <div class="flex items-center">
            <icon name="video" class="stroke-black h-8" />
            <span class="ml-2">{{ form.subject.original_video_filename }}</span>
          </div>
          <video-player
            :video="form.subject.existing_video"
            class="w-lg mb-8"
          />
        </template>

        <file-input
          v-model="form.poster"
          :errors="form.errors.poster"
          class="w-full pb-4 pr-6"
          type="file"
          accept="image/*"
          label="Poster"
        />
        <div
          v-if="form.subject.existing_poster && !form.remove_poster"
          class="w-full pb-4 pr-6"
        >
          <img
            :src="form.subject.existing_poster"
            alt="Poster"
            class="max-h-40"
          />
          <button
            class="btn btn-small btn-red mt-2"
            type="button"
            @click="form.remove_poster = true"
          >
            Sterge posterul
          </button>
        </div>

        <div class="w-full pb-8 pr-6 pt-4">
          <div class="flex items-center justify-between">
            <label class="form-label">Texte</label>
            <button
              class="btn btn-small btn-teal"
              type="button"
              @click="addText()"
            >
              + Adauga text
            </button>
          </div>
          <div class="mt-2 space-y-3">
            <div
              v-for="(_, idx) in form.subject.texts"
              :key="idx"
              class="flex items-start gap-2"
            >
              <textarea
                v-model="form.subject.texts[idx]"
                class="form-input w-full min-h-12"
                placeholder="Text..."
              ></textarea>
              <button
                class="btn btn-small btn-red h-9"
                type="button"
                @click="removeText(idx)"
              >
                Sterge
              </button>
            </div>
          </div>
        </div>
      </template>
      <template v-if="form.subject.type === 'DocumentsSubject'">
        <div class="flex flex-col bg-gray-100 w-full mr-6 mb-3">
          <template
            v-for="document in form.subject.existing_documents"
            :key="document.id"
          >
            <div
              v-if="!form.remove_documents.includes(document.id)"
              class="border-b p-1 hover:bg-gray-300 flex flex-row justify-between"
            >
              <span>
                <i class="ml-0.5 pi pi-file text-xl"></i>{{ document.filename }}
              </span>
              <button
                class="btn-red btn-small ml-2"
                @click="remove(document.id)"
              >
                X
              </button>
            </div>
          </template>
        </div>
        <div class="w-full pr-6">
          <FileUploader v-model:files="form.documents" />
        </div>
      </template>
      <template v-if="form.subject.type === 'QuizSubject'">
        <select-input
          v-model="form.subject.quiz_type"
          class="w-full pb-8 pr-6"
          label="Tip Quiz"
          required
        >
          <option value="practice">Practica</option>
          <option value="course">Curs</option>
          <option value="exam">Examen</option>
        </select-input>
        <div class="w-full pb-8 pr-6 h-fit">
          <label class="form-label">Descriere</label>
          <QuillEditor
            v-model:content="form.subject.description"
            content-type="html"
            theme="snow"
            :toolbar="toolbarOptions"
          />
        </div>
        <div class="w-full pb-8 pr-6">
          <div class="flex items-center justify-between mb-3">
            <label class="form-label">
              Întrebări Quiz
              <template v-if="form.subject.quiz_type === 'exam'">
                <span class="text-sm font-normal text-gray-500 ml-2">
                  (Batch 1: {{ batchCount(1) }} | Batch 2: {{ batchCount(2) }})
                </span>
              </template>
            </label>
            <div class="flex gap-2">
              <button
                v-if="form.quiz_questions.length > 0"
                class="btn btn-small btn-gray cursor-pointer"
                type="button"
                title="Collapse All"
                @click="collapseAllQuestions"
              >
                <i class="pi pi-minus"></i>
              </button>
              <button
                v-if="form.quiz_questions.length > 0"
                class="btn btn-small btn-gray cursor-pointer"
                type="button"
                title="Expand All"
                @click="expandAllQuestions"
              >
                <i class="pi pi-plus"></i>
              </button>
            </div>
          </div>
          <div class="space-y-4">
            <div
              v-for="(question, idx) in form.quiz_questions"
              :key="idx"
              class="border rounded bg-white"
              :class="{
                'border-gray-300': !questionHasErrors(idx),
                'border-red-500': questionHasErrors(idx),
              }"
            >
              <div
                class="flex items-center justify-between p-4 cursor-pointer"
                @click="toggleQuestion(idx)"
              >
                <div class="flex items-center gap-2">
                  <i
                    class="pi text-gray-600"
                    :class="
                      expandedQuestions[idx]
                        ? 'pi-chevron-down'
                        : 'pi-chevron-right'
                    "
                  ></i>
                  <h4 class="font-semibold">
                    Î-{{ idx + 1 }}
                    <span
                      v-if="question.text"
                      class="font-normal text-gray-600"
                    >
                      - {{ question.text.substring(0, 50)
                      }}{{ question.text.length > 50 ? '...' : '' }}
                    </span>
                  </h4>
                  <span
                    v-if="questionHasErrors(idx)"
                    class="text-red-500 text-sm"
                  >
                    <i class="pi pi-exclamation-triangle"></i>
                    {{ getQuestionErrorCount(idx) }} erori
                  </span>
                </div>
                <div
                  v-if="form.subject.quiz_type === 'exam'"
                  class="flex items-center gap-1"
                >
                  <button
                    class="btn-small cursor-pointer border rounded-sm font-bold text-sm whitespace-nowrap"
                    :class="
                      hasBatch(question, 1)
                        ? 'bg-teal-700 text-white border-teal-700 hover:bg-teal-500'
                        : 'bg-white text-gray-600 border-gray-400 hover:bg-gray-100'
                    "
                    type="button"
                    @click.stop="toggleBatch(question, 1)"
                  >
                    B1
                  </button>
                  <button
                    class="btn-small cursor-pointer border rounded-sm font-bold text-sm whitespace-nowrap"
                    :class="
                      hasBatch(question, 2)
                        ? 'bg-teal-700 text-white border-teal-700 hover:bg-teal-500'
                        : 'bg-white text-gray-600 border-gray-400 hover:bg-gray-100'
                    "
                    type="button"
                    @click.stop="toggleBatch(question, 2)"
                  >
                    B2
                  </button>
                </div>
              </div>
              <div v-show="expandedQuestions[idx]" class="p-4 pt-0 space-y-3">
                <textarea-input
                  v-model="question.text"
                  class="w-full"
                  label="Textul întrebării"
                  :rows="2"
                  :class="{
                    'border-red-500': questionErrors[idx]?.text,
                  }"
                />
                <text-input
                  v-model="question.right_answer"
                  class="w-full"
                  label="Răspuns corect"
                  :class="{
                    'border-red-500': questionErrors[idx]?.right_answer,
                  }"
                />
                <div class="space-y-2">
                  <text-input
                    v-model="question.wrong_answers[0]"
                    class="w-full"
                    label="Răspuns greșit 1"
                    :class="{
                      'border-red-500': questionErrors[idx]?.wrong_answer_1,
                    }"
                  />
                  <text-input
                    v-model="question.wrong_answers[1]"
                    class="w-full"
                    label="Răspuns greșit 2"
                    :class="{
                      'border-red-500': questionErrors[idx]?.wrong_answer_2,
                    }"
                  />
                </div>
                <div class="flex justify-end mt-2">
                  <button
                    class="btn btn-small btn-red"
                    type="button"
                    @click="removeQuestion(idx)"
                  >
                    Șterge
                  </button>
                </div>
              </div>
            </div>
          </div>
          <button
            class="btn btn-small btn-teal mt-4"
            type="button"
            @click="addQuestion()"
          >
            + Adaugă întrebare
          </button>
        </div>
      </template>
    </div>
    <slot />
  </form>
</template>

<script>
import TextInput from '@/Shared/TextInput.vue';
import TextareaInput from '@/Shared/TextareaInput.vue';
import FileInput from '../../Shared/FileInput.vue';
import SelectInput from '@/Shared/SelectInput.vue';
import Icon from '@/Shared/Icon.vue';
import VideoPlayer from '@/Shared/VideoPlayer.vue';
import FileUploader from '@/Shared/FileUploader.vue';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
  components: {
    TextInput,
    TextareaInput,
    FileInput,
    Icon,
    VideoPlayer,
    SelectInput,
    FileUploader,
    QuillEditor,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    max: {
      type: Number,
      required: true,
    },
    edit: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      expandedQuestions: {},
      questionErrors: {},
      toolbarOptions: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ header: 1 }, { header: 2 }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ['link'],
        ['clean'],
      ],
    };
  },
  computed: {
    form: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit('update:modelValue', val);
      },
    },
    subjectTypeName() {
      switch (this.form.subject.type) {
        case 'VideoSubject':
          return 'Video';
        case 'DocumentsSubject':
          return 'Documents';
        case 'FeedbackSubject':
          return 'Feedback';
        case 'QuizSubject':
          return 'Quiz';
        default:
          return '';
      }
    },
  },
  watch: {
    'form.subject.type': {
      handler(newType) {
        // Set default quiz_type when type changes to QuizSubject
        if (newType === 'QuizSubject' && !this.form.subject.quiz_type) {
          this.form.subject.quiz_type = 'practice';
        }
      },
      immediate: true,
    },
    'form.quiz_questions': {
      handler() {
        // Clear errors for fields that are now filled
        if (Object.keys(this.questionErrors).length > 0) {
          this.clearFilledFieldErrors();
        }
      },
      deep: true,
    },
  },
  methods: {
    handleSubmit() {
      // Validate quiz questions if it's a quiz subject
      if (this.form.subject.type === 'QuizSubject') {
        const isValid = this.validateQuestions();
        if (!isValid) {
          // Don't submit if validation fails
          return;
        }
      }
      // Emit submit event if validation passes (or not a quiz)
      this.$emit('submit');
    },
    remove(documentId) {
      this.form.remove_documents.push(documentId);
    },
    addText() {
      this.form.subject.texts.push('');
    },
    removeText(index) {
      this.form.subject.texts.splice(index, 1);
    },
    addQuestion() {
      const newIndex = this.form.quiz_questions.length;
      this.form.quiz_questions.push({
        text: '',
        right_answer: '',
        wrong_answers: ['', ''],
        question_batch: [],
      });
      // Expand newly added question
      this.expandedQuestions = { ...this.expandedQuestions, [newIndex]: true };
    },
    removeQuestion(index) {
      this.form.quiz_questions.splice(index, 1);
      // Clean up expanded state
      const newExpanded = {};
      Object.keys(this.expandedQuestions).forEach((key) => {
        const idx = parseInt(key);
        if (idx < index) {
          newExpanded[idx] = this.expandedQuestions[idx];
        } else if (idx > index) {
          newExpanded[idx - 1] = this.expandedQuestions[idx];
        }
      });
      this.expandedQuestions = newExpanded;
    },
    toggleQuestion(index) {
      this.expandedQuestions = {
        ...this.expandedQuestions,
        [index]: !this.expandedQuestions[index],
      };
    },
    collapseAllQuestions() {
      const collapsed = {};
      this.form.quiz_questions.forEach((_, idx) => {
        collapsed[idx] = false;
      });
      this.expandedQuestions = collapsed;
    },
    expandAllQuestions() {
      const expanded = {};
      this.form.quiz_questions.forEach((_, idx) => {
        expanded[idx] = true;
      });
      this.expandedQuestions = expanded;
    },
    validateQuestions() {
      const errors = {};
      this.form.quiz_questions.forEach((question, idx) => {
        const questionErrors = {};

        if (!question.text || question.text.trim() === '') {
          questionErrors.text = true;
        }

        if (!question.right_answer || question.right_answer.trim() === '') {
          questionErrors.right_answer = true;
        }

        if (
          !question.wrong_answers ||
          !question.wrong_answers[0] ||
          question.wrong_answers[0].trim() === ''
        ) {
          questionErrors.wrong_answer_1 = true;
        }

        if (
          !question.wrong_answers ||
          !question.wrong_answers[1] ||
          question.wrong_answers[1].trim() === ''
        ) {
          questionErrors.wrong_answer_2 = true;
        }

        if (Object.keys(questionErrors).length > 0) {
          errors[idx] = questionErrors;
        }
      });

      this.questionErrors = errors;
      return Object.keys(errors).length === 0;
    },
    clearFilledFieldErrors() {
      const updatedErrors = { ...this.questionErrors };

      this.form.quiz_questions.forEach((question, idx) => {
        if (updatedErrors[idx]) {
          const questionErrors = { ...updatedErrors[idx] };

          if (question.text && question.text.trim() !== '') {
            delete questionErrors.text;
          }

          if (question.right_answer && question.right_answer.trim() !== '') {
            delete questionErrors.right_answer;
          }

          if (
            question.wrong_answers &&
            question.wrong_answers[0] &&
            question.wrong_answers[0].trim() !== ''
          ) {
            delete questionErrors.wrong_answer_1;
          }

          if (
            question.wrong_answers &&
            question.wrong_answers[1] &&
            question.wrong_answers[1].trim() !== ''
          ) {
            delete questionErrors.wrong_answer_2;
          }

          if (Object.keys(questionErrors).length === 0) {
            delete updatedErrors[idx];
          } else {
            updatedErrors[idx] = questionErrors;
          }
        }
      });

      this.questionErrors = updatedErrors;
    },
    questionHasErrors(index) {
      return this.questionErrors[index] !== undefined;
    },
    getQuestionErrorCount(index) {
      if (!this.questionErrors[index]) return 0;
      return Object.keys(this.questionErrors[index]).length;
    },
    hasBatch(question, batch) {
      return (question.question_batch || []).includes(batch);
    },
    toggleBatch(question, batch) {
      if (!question.question_batch) {
        question.question_batch = [];
      }
      const idx = question.question_batch.indexOf(batch);
      if (idx >= 0) {
        question.question_batch.splice(idx, 1);
      } else {
        question.question_batch.push(batch);
      }
    },
    batchCount(batch) {
      return this.form.quiz_questions.filter((q) =>
        (q.question_batch || []).includes(batch),
      ).length;
    },
  },
};
</script>
